import { Link, router, usePage } from '@inertiajs/react'
import { ReactNode, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  HiArrowDownTray,
  HiChevronDown,
  HiOutlineArrowLeft,
  HiOutlineCalendar,
  HiOutlineEye,
  HiOutlineLink,
  HiOutlineMapPin,
} from 'react-icons/hi2'
import TopBar from './TopBar'

type EventStatus = 'upcoming' | 'ongoing' | 'completed'

type EventPayload = {
  id: number
  title: string
  slug: string
  location: string
  date_range: string
  status: EventStatus
  banner_url: string | null
}

type EventSummary = {
  title: string
  slug: string
}

type ActiveTab = 'analytics' | 'leads' | 'stages' | 'setup' | 'settings'

type CurrentUser = {
  id: number
  name: string
  email: string
  role: string
  initials: string | null
  photo_url: string | null
}

type SharedProps = {
  current_user: CurrentUser | null
}

type Props = {
  event: EventPayload
  events: EventSummary[]
  active: ActiveTab
  children: ReactNode
}

export default function EventLayout({ event, events, active, children }: Props) {
  const { props } = usePage<SharedProps & { [key: string]: unknown }>()
  const user = props.current_user
  if (!user) return null
  const isManager = user.role === 'manager'

  return (
    <div className="min-h-screen bg-ce-off-white font-sans">
      {/* Desktop topbar (logo + profile) */}
      <div className="hidden md:block">
        <TopBar currentUser={user} />
      </div>

      {/* Mobile topbar: back + event switcher (no logo, no profile) */}
      <header className="bg-ink md:hidden">
        <div className="flex items-center gap-3 px-5 py-3">
          <Link
            href="/dashboard"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Back to dashboard"
          >
            <HiOutlineArrowLeft className="h-5 w-5" />
          </Link>
          <div className="min-w-0 flex-1">
            <EventSwitcher
              events={events}
              current={event}
              activeTab={active}
              variant="dark"
            />
          </div>
        </div>
      </header>

      {/* Mobile banner image */}
      {event.banner_url && (
        <img
          src={event.banner_url}
          alt=""
          aria-hidden="true"
          className="block h-48 w-full object-cover md:hidden"
        />
      )}

      {/* Desktop sub-header row: back + switcher + action buttons */}
      <div className="hidden bg-white shadow-sm md:block">
        <div className="mx-auto flex w-full max-w-[1440px] items-center gap-3 px-5 py-3 md:px-10 md:py-4">
          <Link
            href="/dashboard"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 transition hover:bg-gray-50"
            aria-label="Back to dashboard"
          >
            <HiOutlineArrowLeft className="h-5 w-5" />
          </Link>

          <EventSwitcher events={events} current={event} activeTab={active} />

          <EventActionButtons slug={event.slug} />
        </div>
      </div>

      {/* Event hero: title, status, meta */}
      <div className="mx-auto w-full max-w-[1440px] px-5 pb-6 pt-8 md:px-10 md:pb-8 md:pt-12">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight text-ink md:text-[40px] md:leading-[1.2]">
            {event.title}
          </h1>
          <StatusBadge status={event.status} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1 font-display text-sm text-black/60">
            <HiOutlineCalendar className="h-4 w-4" aria-hidden="true" />
            {event.date_range}
          </span>
          <span className="inline-flex items-center gap-1 font-display text-sm text-gray-600">
            <HiOutlineMapPin className="h-4 w-4" aria-hidden="true" />
            {event.location}
          </span>
        </div>

        {/* Mobile-only action buttons (desktop shows them in the sub-header) */}
        <div className="mt-5 md:hidden">
          <EventActionButtons slug={event.slug} />
        </div>
      </div>

      {/* Event tab strip */}
      <EventTabStrip active={active} slug={event.slug} showSetup={isManager} />

      {/* Tab content */}
      <div className="mx-auto w-full max-w-[1440px] px-5 py-8 md:px-10 md:py-10">
        {children}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// EventActionButtons — sub-header right-side actions.
//   Export CSV  : downloads all visible leads as CSV
//   Preview Form: opens `/:slug` (the public form) in a new tab
//   Copy Link   : copies the full public form URL to clipboard
// ────────────────────────────────────────────────────────────────

function EventActionButtons({ slug }: { slug: string }) {
  const formUrl = typeof window !== 'undefined' ? `${window.location.origin}/${slug}` : `/${slug}`

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(formUrl)
      toast.success('Link copied to clipboard')
    } catch {
      toast.error('Could not copy link')
    }
  }

  const previewForm = () => {
    window.open(`/${slug}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="flex items-center gap-2 md:ml-auto">
      <button
        type="button"
        onClick={copyLink}
        className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-ink px-3 py-2.5 font-display text-sm font-medium text-white transition hover:bg-midnight-green md:flex-none md:px-4"
      >
        Copy Link
        <HiOutlineLink className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={previewForm}
        className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-2.5 font-display text-sm font-medium text-gray-700 transition hover:bg-gray-50 md:flex-none md:px-4"
      >
        View Form
        <HiOutlineEye className="h-4 w-4" aria-hidden="true" />
      </button>
      <a
        href={`/events/${slug}/export.csv`}
        className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-2.5 font-display text-sm font-medium text-gray-700 transition hover:bg-gray-50 md:flex-none md:px-4"
      >
        CSV
        <HiArrowDownTray className="h-4 w-4" aria-hidden="true" />
      </a>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// EventSwitcher — dropdown listing all events. Clicking one navigates
// to the same tab on that event. Single caller (EventLayout) → inline.
// ────────────────────────────────────────────────────────────────

function EventSwitcher({
  events,
  current,
  activeTab,
  variant = 'light',
}: {
  events: EventSummary[]
  current: EventPayload
  activeTab: ActiveTab
  variant?: 'light' | 'dark'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleSelect = (slug: string) => {
    setOpen(false)
    if (slug === current.slug) return
    router.visit(`/events/${slug}/${activeTab === 'setup' ? 'form-setup' : activeTab}`)
  }

  const isDark = variant === 'dark'
  const triggerClasses = isDark
    ? 'flex w-full items-center justify-between gap-2 rounded-md border border-white/30 bg-white/10 px-3 py-2 font-display text-sm font-medium text-white transition hover:bg-white/20'
    : 'flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 font-display text-sm font-medium text-gray-900 transition hover:bg-gray-50'
  const chevronClasses = isDark ? 'h-4 w-4 text-white/80' : 'h-4 w-4 text-gray-500'
  const titleClasses = isDark ? 'truncate' : 'max-w-[180px] truncate'

  return (
    <div ref={ref} className={`relative ${isDark ? 'w-full' : ''}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={triggerClasses}>
        <span className={titleClasses}>{current.title}</span>
        <HiChevronDown className={chevronClasses} aria-hidden="true" />
      </button>

      {open && (
        <div
          className={`absolute left-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ${
            isDark ? 'w-full' : 'w-64'
          }`}
        >
          <ul role="listbox" className="max-h-80 overflow-y-auto py-1">
            {events.map((e) => {
              const isCurrent = e.slug === current.slug
              return (
                <li key={e.slug}>
                  <button
                    type="button"
                    onClick={() => handleSelect(e.slug)}
                    className={`block w-full px-4 py-2 text-left font-display text-sm transition ${
                      isCurrent
                        ? 'bg-gray-50 font-medium text-midnight-green'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {e.title}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// EventTabStrip — Analytics / Leads / Stages with underlined active tab.
// Separate from the app-level TabStrip (different tabs, different scope).
// ────────────────────────────────────────────────────────────────

function EventTabStrip({
  active,
  slug,
  showSetup,
}: {
  active: ActiveTab
  slug: string
  showSetup: boolean
}) {
  const tabs: Array<{ id: ActiveTab; label: string; slug: string }> = [
    { id: 'analytics', label: 'Analytics', slug: 'analytics' },
    { id: 'leads', label: 'Leads', slug: 'leads' },
    { id: 'stages', label: 'Stages', slug: 'stages' },
  ]
  if (showSetup) {
    tabs.push({ id: 'setup', label: 'Form', slug: 'form-setup' })
    tabs.push({ id: 'settings', label: 'Settings', slug: 'settings' })
  }

  return (
    <nav className="border-b border-gray-200 bg-ce-off-white">
      <div className="mx-auto flex w-full max-w-[1440px] gap-6 px-5 md:px-10">
        {tabs.map((tab) => {
          const isActive = tab.id === active
          return (
            <Link
              key={tab.id}
              href={`/events/${slug}/${tab.slug}`}
              className={`relative py-3 font-display font-medium transition ${
                isActive ? 'text-midnight-green' : 'text-gray-500 hover:text-midnight-green'
              }`}
            >
              {tab.label}
              {isActive && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 bg-midnight-green" />
              )}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

function StatusBadge({ status }: { status: EventStatus }) {
  const styles: Record<EventStatus, string> = {
    upcoming: 'bg-white text-ink border border-gray-300',
    ongoing: 'bg-[#93D500] text-ink',
    completed: 'bg-ink text-white',
  }
  const label = status.charAt(0).toUpperCase() + status.slice(1)
  return (
    <span
      className={`rounded-[10px] px-2.5 py-0.5 font-display text-xs font-medium ${styles[status]}`}
    >
      {label}
    </span>
  )
}
